import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { AppConfig } from '@wetransfer/transfer/core-bundle/config/app-config';
import Cookie from '@wetransfer/transfer/modules/concorde-cookie';
import { State } from '@wetransfer/transfer/store/types';

import {
  Team,
  Member,
  useCurrentUserTeamsQuery,
} from './useCurrentUserTeamsQuery';

function setCurrentTeamCookie(teamId: string, domain: string): void {
  Cookie.set(AppConfig.cookies.currentTeam.name, teamId, {
    days: 365,
    domain,
    path: '/',
  });
}

function selectCurrentTeam(teams?: Team[], auth0UserId?: string): Team {
  if (!teams || !auth0UserId) return;

  const currentTeamId = Cookie.get(AppConfig.cookies.currentTeam.name);
  const currentTeam = teams.find((team) => team.id === currentTeamId);

  if (currentTeam) return currentTeam;

  return teams.find((team) => team.creatorId === auth0UserId);
}

export function currentTeamId(): string {
  return Cookie.get(AppConfig.cookies.currentTeam.name);
}

export function removeCurrentTeamCookie(domain?: string): void {
  const path = '/';
  const options = domain ? { path, domain } : { path };
  Cookie.unset(AppConfig.cookies.currentTeam.name, options);
}

export function useCurrentTeam(): {
  currentTeam: Team;
  allTeams: Team[];
  fetching: boolean;
  getUserByAuth0Id(auth0UserId: string): Member;
  getUserByEmail(email: string): Member;
  getUserIndexByEmail(email: string): number;
  onTeamSwitch(newTeamId: string): void;
  isCurrentTeamOfMany: boolean;
  userIsTeamMember(auth0UserId: string): boolean;
  userIsTeamOwner(auth0UserId: string): boolean;
} {
  const { data, fetching } = useCurrentUserTeamsQuery();

  const { domain, auth0UserId } = useSelector((state: State) => ({
    domain: state.session.domain,
    auth0UserId: state.auth.auth0UserId,
  }));

  const currentTeam = selectCurrentTeam(data?.teams, auth0UserId);
  const allTeams = data?.teams;

  useEffect(() => {
    const currentTeamCookie = Cookie.get(AppConfig.cookies.currentTeam.name);
    const currentTeamId = currentTeam?.id;

    if (currentTeamId && currentTeamCookie !== currentTeamId) {
      setCurrentTeamCookie(currentTeamId, domain);
    }
  }, [currentTeam?.id, domain]);

  function getUserByAuth0Id(auth0UserId: string): Member {
    return currentTeam?.members.find(
      (member) => member.user.auth0UserId === auth0UserId
    );
  }

  function getUserByEmail(email: string): Member {
    return currentTeam?.members.find((member) => member.user.email === email);
  }

  function getUserIndexByEmail(email: string): number {
    return currentTeam?.members.findIndex(
      (member) => member.user.email === email
    );
  }

  function onTeamSwitch(newTeamId: string) {
    setCurrentTeamCookie(newTeamId, domain);
  }

  const isCurrentTeamOfMany = currentTeam?.members.length > 1;

  const userIsTeamMember = (auth0UserId: string) => {
    return Boolean(currentTeam?.id) && currentTeam?.creatorId !== auth0UserId;
  };

  const userIsTeamOwner = (auth0UserId: string) => {
    return (
      Boolean(currentTeam?.id) &&
      currentTeam?.creatorId === auth0UserId &&
      isCurrentTeamOfMany
    );
  };

  return {
    currentTeam,
    allTeams,
    fetching,
    getUserByAuth0Id,
    getUserByEmail,
    getUserIndexByEmail,
    onTeamSwitch,
    isCurrentTeamOfMany,
    userIsTeamMember,
    userIsTeamOwner,
  };
}
